// CoachHost — the coaching / guided-tour impure-shell host, extracted from
// AppShellHost (the composition root) so the onboarding feature owns its own
// file instead of colliding on the shared hot file (split-the-hot-file,
// .claude/analysis/dev-orchestration-model.md §10a). Mirrors useEditorHost /
// useNavHost: the shell threads its deps in through ONE named `CoachHostDeps`
// and this hook hands back the ready tour `overlay` element (the GuidedTour),
// the coaching model's `isFreshForAuthor` signal (which the hero surface also
// reads, so the shell relays it), the derived `arcProminence` (Dm4), the
// tour intents (`startTour`, and `viewTour` for the view on screen, which the
// Help menu offers) and a `resetCoaching` action for the dev reset-onboarding
// command.
//
// The guided tours (viewTours.ts): one per main view. The first time a view
// with a tour opens, its tour is offered once, quietly (a toast with "Take
// the tour" that goes by itself; never started on its own, Dm9), and the
// view is remembered per device (tourSeen.ts), so a reload never offers it
// again. The Help menu's "Take a guided tour" (pen Q68) and the chat's link
// start it any time; until it is taken, the Help menu marks it New. Pure-shell discipline: the running step, the offer, its one
// persistence write and the getBoundingClientRect MEASUREMENT live here; the
// GuidedTour component and the tours are pure.

import {
    type ReactNode,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import {
    type ActivationProgress,
    type ArcProminence,
    arcProminence,
    authorExperience,
    type OnboardingSurface,
    onboardingSurface,
    thisAuthor,
    type WorkspaceStatus,
    workspaceStatus,
} from "../../app/coaching.js";
import type { HostNotice } from "../../app/frame.js";
import type { Library } from "../../app/ports.js";
import { useToursSeen, useToursTaken } from "../../app/tourSeen.js";
import {
    GuidedTour,
    type TargetRect,
    type TourLayout,
    type TourPhase,
    type TourTargetId,
} from "../GuidedTour/GuidedTour.js";
import {
    offerFor,
    type TourView,
    useViewTours,
    type ViewTour,
} from "../GuidedTour/viewTours.js";
import type { RecentThread } from "../WorkspaceLanding/WorkspaceLanding.js";

// How the guided tour finds each REAL spotlight target in the DOM: the
// view's own classes (the first one on screen wins). Host-only — used by
// `measureTourTargets` (a sanctioned DOM read).
const TOUR_TARGET_SELECTORS: Record<TourTargetId, string> = {
    "projects-toolbar": ".bilda-projects__nav",
    "new-project": ".bilda-sidebar__cta",
    "project-open": ".bilda-projects__card, .bilda-projects__table tbody tr",
    "studio-outline": ".bilda-pages__outline",
    "studio-page": ".bilda-pages__paper",
    "studio-create": ".bilda-pages__outline-foot",
    "studio-panel": ".bilda-studio-panel__switch",
    "studio-export": ".bilda-pages__top > .bilda-btn",
    "search-field": ".bilda-search__field",
    "search-filters": ".bilda-search__filters",
    "search-results": ".bilda-search__list",
};
const TOUR_TARGET_IDS: readonly TourTargetId[] = [
    "projects-toolbar",
    "new-project",
    "project-open",
    "studio-outline",
    "studio-page",
    "studio-create",
    "studio-panel",
    "studio-export",
    "search-field",
    "search-filters",
    "search-results",
];
// Snapshot each present target's viewport rect (getBoundingClientRect is an
// impure MEASUREMENT — called only from the host's step effect). The first
// match with a size is the one on screen (the menu's peek copy is not); a
// missing element is simply skipped, so an absent target degrades to a
// centred popup rather than a crash.
const measureTourTargets = (): Partial<Record<TourTargetId, TargetRect>> => {
    const rects: Partial<Record<TourTargetId, TargetRect>> = {};
    for (const id of TOUR_TARGET_IDS) {
        const shown = [
            ...document.querySelectorAll(TOUR_TARGET_SELECTORS[id]),
        ].find((el) => {
            const r = el.getBoundingClientRect();
            return r.width > 0 && r.height > 0;
        });
        if (shown === undefined) continue;
        const r = shown.getBoundingClientRect();
        rects[id] = {
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
        };
    }
    return rects;
};
// One snapshot of everything the tour's placement needs: the targets, the
// window (clientWidth/Height, so a scrollbar is excluded) and the popup's own
// rendered size (offset* ignores transforms).
const measureTourLayout = (popup: HTMLDivElement | null): TourLayout => ({
    rects: measureTourTargets(),
    viewport: {
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
    },
    popup:
        popup === null
            ? null
            : { width: popup.offsetWidth, height: popup.offsetHeight },
});

// The dependency surface the coaching host pulls from the shell. `view` is
// the view on screen with a tour (viewTours.ts `tourViewOf`), or none;
// `openView` goes to a view (an offer taken after the author moved on);
// `showNotice` carries the offer; `library` + `conversationSummaries` feed
// the per-author WorkspaceStatus; `tipsDismissed` is the coaching model's
// Advanced-ladder signal.
export type CoachHostDeps = {
    readonly view: TourView | null;
    readonly openView: (view: TourView) => void;
    readonly showNotice: (notice: HostNotice) => void;
    readonly library: Library | null;
    readonly conversationSummaries: readonly RecentThread[];
    readonly tipsDismissed: boolean;
};

export type CoachHostApi = {
    // The coaching model's per-author "fresh workspace" signal — the hero
    // surface's landing state keys on it too, so the shell relays this value
    // into HeroHost to keep the two surfaces in step.
    readonly isFreshForAuthor: boolean;
    // The ready tour overlay (the GuidedTour). Renders nothing at rest (idle).
    readonly overlay: ReactNode;
    // WHICH first-run CENTRE surface a coached newcomer should see — the pure
    // (experience × status × progress) decision (app/coaching.ts). `none` at
    // rest. The shell routes each case to its component in the correct centre
    // slot; the placement stays the shell's.
    readonly onboardingSurface: OnboardingSurface;
    // How loudly the hero outcome arc should present itself — derived from the
    // `AuthorExperience` ladder (app/coaching.ts), no separate toggle. Relayed
    // for the hero surface to thread into its arc.
    readonly arcProminence: ArcProminence;
    // Start a view's tour from its first step (the offer's "Take the tour"),
    // going to that view first if the author has moved on. It shows while
    // that view is on screen.
    readonly startTour: (view: TourView) => void;
    // The tour of the view on screen (the Help menu's row, the chat's link);
    // null when the view has none.
    readonly viewTour: ViewTour | null;
    // Forget which views offered their tour and close any open tour (dev
    // reset-onboarding). Own intent, own method — the shell cannot reach the
    // host's private state otherwise.
    readonly resetCoaching: () => void;
};

// The tour being walked: which view's, at which step.
type RunningTour = { readonly view: TourView; readonly index: number };

export const useCoachHost = (deps: CoachHostDeps): CoachHostApi => {
    const {
        view,
        openView,
        showNotice,
        library,
        conversationSummaries,
        tipsDismissed,
    } = deps;
    const tours = useViewTours();
    const [seen, recordSeen] = useToursSeen();
    const [taken, recordTaken] = useToursTaken();

    const [running, setRunning] = useState<RunningTour | null>(null);
    const [tourLayout, setTourLayout] = useState<TourLayout | null>(null);
    const tourPopupRef = useRef<HTMLDivElement>(null);
    // The current author's PICTURE of the workspace — the WorkspaceStatus axis
    // (per-workspace, domain-derived; app/coaching.ts, IDD f95f4542), NOT a shell
    // heuristic. `Fresh` = this author sees no resources or conversations yet.
    // Reads are already author-scoped (single-tenant MVP), so the footprint is
    // `thisAuthor`'s. `library === null` (not yet loaded) yields `null` —
    // deliberately NOT fresh, so nothing flashes before the read lands.
    // `lastActivityAt: null` keeps the Stale split off until a clock is wired.
    const authorWorkspace: WorkspaceStatus | null =
        library === null
            ? null
            : workspaceStatus(thisAuthor, {
                  resourceCount:
                      library.sources.length +
                      library.materials.length +
                      library.exports.length,
                  conversationCount: conversationSummaries.length,
                  lastActivityAt: null,
              });
    const isFreshForAuthor = authorWorkspace?.type === "fresh";
    // The author's EXPERIENCE ladder (device-local axis; IDD f95f4542), derived
    // from a real dismissal affordance ("stäng av tips" → Advanced).
    const experience = authorExperience({ tipsDismissed });
    const prominence = arcProminence(experience);

    // The author's ACTIVATION PROGRESS — a pure projection of the Library, NOT a
    // stored onboarding flag (deleting the last source flips `hasSource` back).
    const progress: ActivationProgress =
        library === null
            ? { hasSource: false, hasMaterial: false, hasPackage: false }
            : {
                  hasSource: library.sources.length > 0,
                  hasMaterial: library.materials.length > 0,
                  hasPackage: library.exports.length > 0,
              };
    const surface: OnboardingSurface =
        authorWorkspace === null
            ? { type: "none" }
            : onboardingSurface(experience, authorWorkspace, progress);

    const startTour = (tourView: TourView) => {
        if (tourView !== view) openView(tourView);
        recordSeen({ type: "tour_started", view: tourView });
        recordTaken({ type: "tour_started", view: tourView });
        setRunning({ view: tourView, index: 0 });
    };
    const viewTour: ViewTour | null =
        view === null
            ? null
            : {
                  name: tours[view].name,
                  isNew: !taken.has(view),
                  start: () => startTour(view),
              };

    // The quiet offer: once per view, once the library has loaded (so it
    // never lands on a half-drawn page), never over a running tour. Recorded
    // as it shows, so neither its going by itself nor a reload brings it
    // back.
    const offer =
        library === null || running !== null ? null : offerFor(view, seen);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `offer` is the whole trigger; showNotice and recordSeen are the shell's stable doors.
    useEffect(() => {
        if (offer === null) return;
        recordSeen({ type: "tour_offered", view: offer });
        showNotice({ type: "tour-offered", view: offer });
    }, [offer]);

    // Every terminal route (×, Esc, a click on the veil, the last step's
    // Done) ends the same way: idle.
    const endTour = () => setRunning(null);
    const onTourBack = () =>
        setRunning((r) =>
            r !== null && r.index > 0 ? { ...r, index: r.index - 1 } : r,
        );
    const onTourNext = () =>
        setRunning((r) =>
            r !== null && r.index < tours[r.view].steps.length - 1
                ? { ...r, index: r.index + 1 }
                : r,
        );

    // A tour shows only over its own view: leave the view and it is idle,
    // so nothing lingers over another page.
    const shown = running !== null && running.view === view ? running : null;
    const tourPhase: TourPhase =
        shown === null
            ? { type: "idle" }
            : { type: "step", index: shown.index };
    // The step whose target must be measured (-1 when nothing is showing) — the
    // remeasure effect's re-run signal, with the view.
    const tourStepIndex = shown === null ? -1 : shown.index;

    // Measure the REAL spotlight targets, the window and the popup at the
    // impure host edge (sanctioned layout MEASUREMENTS, not buried side
    // effects) whenever a step is showing, and re-measure on resize/scroll and
    // whenever the popup's size changes, so the ring stays aligned and the
    // popup stays inside the window. A layout effect so the first frame is
    // already placed. The pure component never touches the DOM.
    // biome-ignore lint/correctness/useExhaustiveDependencies: the view is a re-run trigger: its step 0 points at another page's elements.
    useLayoutEffect(() => {
        if (tourStepIndex < 0) return;
        const remeasure = () =>
            setTourLayout(measureTourLayout(tourPopupRef.current));
        remeasure();
        window.addEventListener("resize", remeasure);
        window.addEventListener("scroll", remeasure, true);
        const popup = tourPopupRef.current;
        const observer =
            popup === null || typeof ResizeObserver === "undefined"
                ? null
                : new ResizeObserver(remeasure);
        if (popup !== null) observer?.observe(popup);
        return () => {
            window.removeEventListener("resize", remeasure);
            window.removeEventListener("scroll", remeasure, true);
            observer?.disconnect();
        };
    }, [tourStepIndex, shown?.view]);

    // Dev reset-onboarding: forget the offered views and close any open
    // tour. The shell clears its own `tipsDismissed` alongside this call.
    const resetCoaching = () => {
        recordSeen({ type: "tours_reset" });
        recordTaken({ type: "tours_reset" });
        setRunning(null);
    };

    const overlay: ReactNode = (
        <GuidedTour
            tour={tours[shown?.view ?? "projects"]}
            phase={tourPhase}
            layout={tourLayout}
            popupRef={tourPopupRef}
            onBack={onTourBack}
            onNext={onTourNext}
            onSkip={endTour}
        />
    );

    return {
        isFreshForAuthor,
        overlay,
        resetCoaching,
        onboardingSurface: surface,
        arcProminence: prominence,
        startTour,
        viewTour,
    };
};
